import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StorageIcon from "@mui/icons-material/Storage";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import { getFullApiUrl } from "../../config";
import { getToken } from "../../utils";
import { ExpiredPage } from "../ExpiredPage";

const HISTORY_KEY = "admin_sql_history";
const MAX_HISTORY = 20;
const LIMIT_OPTIONS = [100, 500, 1000, 2000];

// 排查时反复要用的几条：会员/订阅链路的常见入口
const PRESETS = [
  {
    label: "Apple 交易记录（近期）",
    sql: `SELECT processed_at, original_transaction_id, transaction_id,
       notification_type, subtype, product_id, environment
FROM apple_transaction_history
ORDER BY processed_at DESC
LIMIT 100`,
  },
  {
    label: "Apple 订阅现状",
    sql: `SELECT s.user_id, u.email, s.original_transaction_id, s.product_id,
       s.environment, s.status, s.original_purchase_date, s.expires_date,
       s.grace_period_expires_date, s.auto_renew_status, s.is_in_billing_retry,
       s.created_at, s.updated_at
FROM apple_subscriptions s
LEFT JOIN users u ON u.id = s.user_id
ORDER BY s.updated_at DESC
LIMIT 100`,
  },
  {
    label: "某用户的全部会员来源",
    sql: `WITH target AS (
  SELECT id FROM users WHERE email = '在这里填邮箱'
)
SELECT 'manual' AS source, m.created_at AS start_at, m.membership_end_at AS end_at
FROM manual_memberships m WHERE m.user_id IN (SELECT id FROM target)
UNION ALL
SELECT 'apple', s.original_purchase_date, s.expires_date
FROM apple_subscriptions s
WHERE s.user_id IN (SELECT id FROM target)
  AND s.status IN ('active', 'grace_period', 'billing_retry')
UNION ALL
SELECT 'stripe_sub', ss.current_period_start, ss.current_period_end
FROM stripe_subscriptions ss
WHERE ss.user_id IN (SELECT id FROM target)
  AND ss.status IN ('active', 'trialing', 'canceled')
UNION ALL
SELECT 'stripe_purchase', p.purchased_at, p.expires_at
FROM stripe_purchases p
WHERE p.user_id IN (SELECT id FROM target) AND p.status = 'succeeded'
UNION ALL
SELECT 'zsxq', COALESCE(z.membership_start_at, uz.created_at), z.membership_end_at
FROM user_zsxqs uz
JOIN zsxqs z ON z.id = uz.zsxqs_id
WHERE uz.user_id::uuid IN (SELECT id FROM target)
ORDER BY start_at`,
  },
  {
    label: "按邮箱查用户",
    sql: `SELECT id, email, verified, created_at, updated_at
FROM users
WHERE email ILIKE '%关键字%'
ORDER BY created_at DESC
LIMIT 50`,
  },
];

const formatCell = (value) => {
  if (value === null || value === undefined) return "NULL";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
};

const toCSV = (columns, rows) => {
  const escape = (value) => {
    if (value === null || value === undefined) return "";
    const text = String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  return [
    columns.map(escape).join(","),
    ...rows.map((row) => row.map(escape).join(",")),
  ].join("\n");
};

const SqlConsolePage = () => {
  const [query, setQuery] = useState("SELECT 1");
  const [limit, setLimit] = useState(500);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);
  const [schema, setSchema] = useState([]);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const queryRef = useRef(query);

  queryRef.current = query;

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` },
  });

  const handleApiError = useCallback((err, fallback) => {
    if (err.response?.data?.error === "token has expired") {
      setExpired(true);
      return;
    }
    setError(err.response?.data?.error || fallback);
  }, []);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
      if (Array.isArray(stored)) setHistory(stored);
    } catch {
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        const response = await axios.get(
          getFullApiUrl("/api/v1/admin/sql/schema"),
          authHeaders()
        );
        setSchema(response.data.tables || []);
      } catch {
        // 表结构只是辅助信息，取不到不影响查询
      }
    };
    fetchSchema();
  }, []);

  const pushHistory = useCallback((sql) => {
    setHistory((prev) => {
      const next = [sql, ...prev.filter((item) => item !== sql)].slice(
        0,
        MAX_HISTORY
      );
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const runQuery = useCallback(async () => {
    const sql = queryRef.current.trim();
    if (!sql || running) return;

    setRunning(true);
    setError(null);
    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/admin/sql/query"),
        { query: sql, limit },
        authHeaders()
      );
      setResult(response.data);
      pushHistory(sql);
    } catch (err) {
      setResult(null);
      handleApiError(err, "查询执行失败");
    } finally {
      setRunning(false);
    }
  }, [limit, running, handleApiError, pushHistory]);

  const handleKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      runQuery();
    }
  };

  const csv = useMemo(
    () => (result ? toCSV(result.columns, result.rows) : ""),
    [result]
  );

  const handleCopy = async () => {
    await navigator.clipboard.writeText(csv);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleDownload = () => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `query-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const clearHistory = () => {
    localStorage.removeItem(HISTORY_KEY);
    setHistory([]);
  };

  if (expired) return <ExpiredPage />;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <StorageIcon color="primary" />
        <Typography variant="h5" component="h1">
          SQL 控制台
        </Typography>
        <Chip label="只读" size="small" color="success" variant="outlined" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        只允许单条 SELECT / WITH 查询，在只读事务中执行，超时 15 秒。含
        password / secret 的列会以 *** 返回。
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {/* 用固定 rows 而不是 minRows：minRows 会走 MUI 的 TextareaAutosize，
            它的测量循环会触发 ResizeObserver loop 警告，而且编辑长 SQL 时
            输入框高度会不停跳动。固定高度 + 内部滚动更稳。 */}
        <TextField
          fullWidth
          multiline
          rows={12}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="SELECT * FROM users LIMIT 10"
          spellCheck={false}
          InputProps={{
            sx: {
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              fontSize: 14,
              alignItems: "flex-start",
              "& textarea": { overflow: "auto !important" },
            },
          }}
        />

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 2 }}
        >
          <Button
            variant="contained"
            startIcon={
              running ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PlayArrowIcon />
              )
            }
            onClick={runQuery}
            disabled={running || !query.trim()}
          >
            {running ? "执行中" : "执行"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            ⌘/Ctrl + Enter
          </Typography>
          <TextField
            select
            size="small"
            label="行数上限"
            value={limit}
            onChange={(event) => setLimit(Number(event.target.value))}
            sx={{ width: 130 }}
          >
            {LIMIT_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary">
          常用查询
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {PRESETS.map((preset) => (
            <Chip
              key={preset.label}
              label={preset.label}
              size="small"
              onClick={() => setQuery(preset.sql)}
              variant="outlined"
            />
          ))}
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          <AlertTitle>查询失败</AlertTitle>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              fontSize: 13,
            }}
          >
            {error}
          </Box>
        </Alert>
      )}

      {result && (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{ px: 2, py: 1.5 }}
          >
            <Typography variant="body2">
              {result.row_count} 行 · {result.elapsed_ms} ms
            </Typography>
            {result.truncated && (
              <Chip
                label={`已截断至 ${result.limit} 行`}
                size="small"
                color="warning"
              />
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={copied ? "已复制" : "复制为 CSV"}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  disabled={!result.row_count}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="下载 CSV">
              <span>
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  disabled={!result.row_count}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Divider />

          {result.row_count === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 3 }}
            >
              查询没有返回任何行。
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {result.columns.map((column, index) => (
                      <TableCell
                        key={`${column}-${index}`}
                        sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                      >
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex} hover>
                      {row.map((cell, cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          sx={{
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                            fontSize: 13,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            maxWidth: 420,
                            color:
                              cell === null || cell === undefined
                                ? "text.disabled"
                                : "text.primary",
                          }}
                        >
                          {formatCell(cell)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {history.length > 0 && (
        <Accordion variant="outlined" disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <HistoryIcon fontSize="small" color="action" />
              <Typography variant="body2">
                查询历史（{history.length}）
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {history.map((item, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  onClick={() => setQuery(item)}
                  sx={{
                    p: 1,
                    cursor: "pointer",
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  {item}
                </Paper>
              ))}
              <Button
                size="small"
                color="inherit"
                startIcon={<DeleteOutlineIcon />}
                onClick={clearHistory}
                sx={{ alignSelf: "flex-start" }}
              >
                清空历史
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {schema.length > 0 && (
        <Accordion variant="outlined" disableGutters sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <StorageIcon fontSize="small" color="action" />
              <Typography variant="body2">表结构（{schema.length}）</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              {schema.map((table) => (
                <Box key={table.name}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, cursor: "pointer" }}
                    onClick={() =>
                      setQuery(`SELECT * FROM ${table.name} LIMIT 20`)
                    }
                  >
                    {table.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {table.columns.join(" · ")}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Container>
  );
};

export default SqlConsolePage;
